import { DecimalPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize, firstValueFrom, map } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { UserAssetsService } from 'src/app/pages/user-asset/user-assets.service';
import { DepositApi } from 'src/app/shared/apis/deposit.api';
import { WalletApi } from 'src/app/shared/apis/wallet.api';
import { CSButtonComponent } from 'src/app/shared/components/cs-button/cs-button.component';
import { CSSelectComponent, ICSOption } from 'src/app/shared/components/cs-select/cs-select.component';
import { CustomizeFormModule } from 'src/app/shared/components/customize-form/customize-form.module';
import {
  DepositAddressInterface,
  DepositAddressParamInterface,
  NetworkSchemaPrefixMapping,
} from 'src/app/shared/interfaces/deposit.interface';
import { ForbidDepositType, ForbidPaymentCategory } from 'src/app/shared/interfaces/wallet.interface';
import { PipesModule } from 'src/app/shared/pipes/pipes.module';
import { CurrencyValuePipe } from '../../../../shared/pipes/currency-value.pipe';
import { WalletDialogType, WalletService } from '../../wallet.service';
import { DepositBonusDropdownComponent } from '../deposit-bonus-dropdown/deposit-bonus-dropdown.component';
import { DepositWithdrawForbidComponent } from '../deposit-withdraw-forbid/deposit-withdraw-forbid.component';

@UntilDestroy()
@Component({
  selector: 'app-deposit-transfer',
  standalone: true,
  imports: [
    DecimalPipe,
    CSButtonComponent,
    CustomizeFormModule,
    PipesModule,
    DepositBonusDropdownComponent,
    CSSelectComponent,
    DepositWithdrawForbidComponent,
  ],
  templateUrl: './deposit-transfer.component.html',
  styleUrl: './deposit-transfer.component.scss',
})
export class DepositTransferComponent implements OnInit {
  constructor(
    public appService: AppService,
    public walletService: WalletService,
    private depositApi: DepositApi,
    private userAssetsService: UserAssetsService,
    private walletApi: WalletApi,
    private currencyValuePipe: CurrencyValuePipe,
  ) {}

  /** network variable */
  //當前選擇的網路
  selectedNetwork = '';
  //網路列表
  networkList: ICSOption<string>[] = [];
  //網路加載狀態
  networkLoading = false;

  /** currency variable */
  //當前選擇的幣種
  private _currentCurrency: string = 'USDT';
  get currentCurrency() {
    return this._currentCurrency;
  }
  set currentCurrency(val: string) {
    this._currentCurrency = val;
    this.updateHint();
  }
  //虛擬貨幣幣種列表
  cryptoList: ICSOption<string>[] = [];
  //貨幣加載狀態
  currencyLoading = false;

  /** crypto address variable */
  // 当前显示的地址
  depositAddress: DepositAddressInterface | null = null;
  // 存款地址加载状态
  addressLoading = false;
  // 紅利加載狀態 從<app-deposit-bonus-dropdown>傳入
  bonusLoading: boolean = false;

  hint = '';

  // 存款禁止加載狀態
  isForbidLoading: boolean = false;
  isDepositForbid: boolean = false;

  async updateHint() {
    if (this.userAssetsService.dpRemainingAmount$.value !== null) {
      const rateData = await firstValueFrom(this.walletApi.getRate(this.currentCurrency, 'EUR'));
      const remaining = this.currencyValuePipe.transform(
        this.userAssetsService.dpRemainingAmount$.value * (rateData?.rates?.[0].rate || 0),
        this.currentCurrency,
      );
      this.hint = `Your remaining deposit limit is ${remaining} ${this.currentCurrency}`;
    }
  }

  async ngOnInit(): Promise<void> {
    // 存款前先判断是否禁止存款
    await this.checkDepositForbid();

    // 檢查是否有暫存資料，有的話就還原
    const restored = this.walletService.preservedDepositState;

    if (restored) {
      this.currentCurrency = restored.currentCurrency;
      this.selectedNetwork = restored.selectedNetwork;
      this.depositAddress = restored.depositAddress;
      this.cryptoList = restored.cryptoList;
      this.networkList = restored.networkList;

      this.walletService.clearDepositState(); // 用完就清掉
    } else {
      this.getCurrencyInfo();
    }
  }

  /**
   * 獲取所有幣種與當前選擇幣種
   */
  getCurrencyInfo() {
    this.currencyLoading = true;

    this.walletService
      .getCryptoList()
      .pipe(untilDestroyed(this))
      .subscribe(result => {
        this.currencyLoading = false;
        this.cryptoList = result.cryptoList;
        this.currentCurrency = this.walletService.targetCurrency || result.currentCurrency;
        this.getNetworkList();
      });
  }

  /**
   * 獲取網路列表
   * 目前直接使用userAssetsService內的method
   * 實際的networkList要在重新整理，m1因為直接bind在html上
   */
  async getNetworkList() {
    this.networkLoading = true;
    this.depositAddress = null;
    this.networkList = await this.walletService.getNetwork('Deposit', this.currentCurrency);
    this.networkLoading = false;
    this.selectedNetwork = this.networkList[0]?.value || '';
    if (this.selectedNetwork) {
      this.getDepositAddress();
    }
  }
  /**
   * cs-select的change currency事件
   */
  currencyChange() {
    this.getNetworkList();
    // 處理幣種變更邏輯
    // console.log('Selected currency:', this.currentCurrency);
    // console.log('Selected networkList:', this.networkList);
  }

  /**
   * cs-select的change network事件
   */
  networkChange() {
    this.getDepositAddress();
  }

  async copyDepositAddress() {
    if (!this.depositAddress) return;
    this.walletService.copy(this.depositAddress.address, 'Copied Address');
    this.walletService.pushInitiateDepositOnce({
      type: 'transfer',
      currency: this.currentCurrency,
      amount: '',
    });
  }

  /**
   * 獲取存款地址
   */
  getDepositAddress() {
    this.addressLoading = true;
    this.depositAddress = null;
    const param: DepositAddressParamInterface = {
      currency: this.currentCurrency,
      network: this.selectedNetwork,
    };

    this.depositApi
      .getDepositAddress(param)
      .pipe(
        map(v => v.data),
        finalize(() => {}),
      )
      .subscribe(res => {
        this.addressLoading = false;
        this.depositAddress = res;
      });
  }

  /**
   * 去Qrcode頁面
   * 暫存當前資料
   */
  goToAddressQr() {
    // 暫存存款資料
    this.walletService.preservedDepositState = {
      currentCurrency: this.currentCurrency,
      cryptoList: this.cryptoList,
      selectedNetwork: this.selectedNetwork,
      networkList: this.networkList,
      depositAddress: this.depositAddress!,
      selectedNetworkSchemaPrefix: NetworkSchemaPrefixMapping[this.selectedNetwork] || '',
    };
    this.walletService.setWalletDialogType(WalletDialogType.ADDRESS_QR);
  }

  /**
   * 是否所有資料都已經加載完成
   *
   * @returns
   */
  isAllLoaded(): boolean {
    return !this.bonusLoading && !this.networkLoading && !this.currencyLoading && !this.addressLoading;
  }

  /**
   * 存款前先判断是否禁止存款
   */
  async checkDepositForbid() {
    this.isForbidLoading = true;
    this.isDepositForbid = await firstValueFrom(
      this.walletApi.forbidPaymentInfo({
        category: ForbidPaymentCategory.Deposit,
        depositType: ForbidDepositType.Digital,
      }),
    );
    this.isForbidLoading = false;
  }
}
